import React, { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import { Plus, Edit, UserX, UserCheck, Trash2, Eye, EyeOff, Building2, Users, X, Check } from 'lucide-react';

const emptyForm = { name: '', username: '', password: '', phone: '', hourly_rate: '', department_ids: [], is_active: 1 };

export default function WorkersPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('workers');
  const { formatCurrency } = useSettings();
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editWorker, setEditWorker] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkWorkerIds, setBulkWorkerIds] = useState([]);
  const [bulkDeptIds, setBulkDeptIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('add');
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [wRes, dRes] = await Promise.all([
        api.get('/workers'),
        api.get('/departments', { params: { active_only: '1' } })
      ]);
      setWorkers(wRes.data);
      setDepartments(dRes.data);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditWorker(null); setForm(emptyForm); setShowPassword(false); setShowModal(true); };

  const openEdit = (w) => {
    setEditWorker(w);
    setForm({ name: w.name, username: w.username, password: '', phone: w.phone||'', hourly_rate: w.hourly_rate||'', department_ids: w.departments?.map(d => d.id)||[], is_active: w.is_active });
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!editWorker && !form.password.trim()) return toast.error('Password is required');
    setSaving(true);
    try {
      if (editWorker) {
        await api.put('/workers/' + editWorker.id, { name: form.name, phone: form.phone, hourly_rate: form.hourly_rate||0, is_active: form.is_active, department_ids: form.department_ids, password: form.password });
        toast.success('Worker updated');
      } else {
        await api.post('/workers', { name: form.name, username: form.username, password: form.password, phone: form.phone, hourly_rate: form.hourly_rate||0, department_ids: form.department_ids });
        toast.success('Worker created');
      }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'An error occurred'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete('/workers/' + deleteTarget.id);
      toast.success('Worker deleted');
      setDeleteTarget(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const toggleActive = async (w) => {
    try {
      await api.put('/workers/' + w.id, { name: w.name, phone: w.phone, hourly_rate: w.hourly_rate||0, is_active: w.is_active ? 0 : 1, department_ids: w.departments?.map(d => d.id)||[] });
      toast.success(w.is_active ? 'Deactivated' : 'Activated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const toggleDept = (id) => setForm(f => ({ ...f, department_ids: f.department_ids.includes(id) ? f.department_ids.filter(d => d !== id) : [...f.department_ids, id] }));
  const toggleBulkWorker = (id) => setBulkWorkerIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleBulkDept = (id) => setBulkDeptIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleBulkSave = async () => {
    if (!bulkWorkerIds.length) return toast.error('Please select workers');
    if (!bulkDeptIds.length) return toast.error('Please select departments');
    setBulkSaving(true);
    try {
      await api.post('/workers/bulk-departments', { worker_ids: bulkWorkerIds, department_ids: bulkDeptIds, action: bulkAction });
      toast.success(`Departments assigned to ${bulkWorkerIds.length} workers`);
      setShowBulkModal(false); setBulkWorkerIds([]); setBulkDeptIds([]); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setBulkSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-3 sm:p-4 md:p-6 max-w-6xl mx-auto space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Workers</h1>
          <p className="text-gray-500 text-xs sm:text-sm">{workers.filter(w => w.is_active).length} active · {workers.filter(w => !w.is_active).length} inactive</p>
        </div>
        <div className="flex gap-2 flex-col sm:flex-row">
          <button onClick={() => { setShowBulkModal(true); setBulkWorkerIds([]); setBulkDeptIds([]); }}
            className="flex items-center justify-center sm:justify-start gap-2 border border-blue-300 text-blue-700 hover:bg-blue-50 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium order-2 sm:order-1">
            <Building2 className="w-4 h-4" /> <span className="hidden sm:inline">Bulk Dept Assign</span><span className="sm:hidden">Bulk</span>
          </button>
          {canWrite && <button onClick={openCreate} className="flex items-center justify-center sm:justify-start gap-2 bg-amber-500 hover:bg-amber-600 text-white px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold order-1 sm:order-2">
            <Plus className="w-4 h-4" /> Add Worker
          </button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {workers.map(w => (
          <div key={w.id} className={`bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-sm border transition-all ${w.is_active ? 'border-gray-100 hover:shadow-md' : 'border-gray-100 opacity-60'}`}>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className={`w-10 sm:w-11 h-10 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0 ${w.is_active ? 'bg-amber-500' : 'bg-gray-400'}`}>
                  {w.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{w.name}</p>
                  <p className="text-xs text-gray-400 font-mono truncate">@{w.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                {canEdit && <button onClick={() => openEdit(w)} className="p-1 sm:p-1.5 hover:bg-amber-100 rounded-lg text-amber-600"><Edit className="w-3.5 sm:w-4 h-3.5 sm:h-4" /></button>}
                <button onClick={() => toggleActive(w)} className={`p-1 sm:p-1.5 rounded-lg ${w.is_active ? 'hover:bg-orange-100 text-orange-500' : 'hover:bg-green-100 text-green-600'}`}>
                  {w.is_active ? <UserX className="w-3.5 sm:w-4 h-3.5 sm:h-4" /> : <UserCheck className="w-3.5 sm:w-4 h-3.5 sm:h-4" />}
                </button>
                {canDelete && <button onClick={() => setDeleteTarget(w)} className="p-1 sm:p-1.5 hover:bg-red-100 rounded-lg text-red-400"><Trash2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" /></button>}
              </div>
            </div>
            {w.phone && <p className="text-xs text-gray-500 mb-1 truncate">📞 {w.phone}</p>}
            {w.hourly_rate > 0 && <p className="text-xs text-amber-600 font-medium mb-1.5">💰 {formatCurrency(w.hourly_rate)}/hr</p>}
            {w.departments?.length > 0 ? (
              <div className="flex flex-wrap gap-1 mb-2">
                {w.departments.map(d => (
                  <span key={d.id} className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: d.color||'#6366F1' }}>{d.name}</span>
                ))}
              </div>
            ) : <p className="text-xs text-gray-300 italic mb-2">No department assigned</p>}
            <div className="mt-2 pt-2 border-t border-gray-50">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${w.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {w.is_active ? '● Active' : '● Inactive'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {workers.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No workers yet — get started with Add Worker</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480}}>
            <div className="modal-header">
              <h2 className="text-lg font-bold">{editWorker ? 'Edit — ' + editWorker.name : 'New Worker'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close" type="button"><X size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body space-y-4">
              <div>
                <label className="field-label">Full Name *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="field-input" placeholder="e.g. Dharam Kaka" />
              </div>
              <div>
                <label className="field-label">Username {!editWorker && '*'}</label>
                <input required={!editWorker} value={form.username} onChange={e => setForm({...form, username: e.target.value})} disabled={!!editWorker}
                  className="field-input"
                  placeholder="Login username" />
              </div>
              <div>
                <label className="field-label">
                  {editWorker ? 'New Password (leave blank = no change)' : 'Password *'}
                </label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} required={!editWorker}
                    value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                    placeholder={editWorker ? 'Leave blank to keep existing' : 'Enter password'}
                    className="field-input" style={{paddingRight:44}} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="field-label">Phone</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="field-input" placeholder="Mobile number" />
              </div>
              <div>
                <label className="field-label">Hourly Rate</label>
                <input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e => setForm({...form, hourly_rate: e.target.value})}
                  placeholder="e.g. 150"
                  className="field-input" />
              </div>
              {editWorker && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <input type="checkbox" id="is_active" checked={!!form.is_active} onChange={e => setForm({...form, is_active: e.target.checked ? 1 : 0})} className="w-4 h-4 accent-amber-600" />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Worker is Active</label>
                </div>
              )}
              <div>
                <label className="field-label">
                  Departments
                  {form.department_ids.length > 0 && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{form.department_ids.length} selected</span>}
                </label>
                <div className="flex flex-wrap gap-2">
                  {departments.map(d => (
                    <button key={d.id} type="button" onClick={() => toggleDept(d.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all flex items-center gap-1 ${form.department_ids.includes(d.id) ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300'}`}
                      style={form.department_ids.includes(d.id) ? { backgroundColor: d.color } : {}}>
                      {form.department_ids.includes(d.id) && <Check className="w-3 h-3" />}
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-primary" style={{flex:1}}>
                  {saving ? 'Saving...' : editWorker ? 'Update Worker' : 'Create Worker'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="confirm-box">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"><Trash2 className="w-6 h-6 text-red-500" /></div>
              <div>
                <p className="font-bold text-gray-900">Delete "{deleteTarget.name}"?</p>
                <p className="text-xs text-gray-500">This is a permanent action</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-red-700">⚠️ If worker has tasks, time logs or queries, <strong>they cannot be deleted</strong> — <strong>Deactivate</strong> them instead.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleDelete} disabled={deleting} className="btn-danger" style={{flex:1}}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button onClick={() => setDeleteTarget(null)} className="btn-secondary" style={{flex:1}}>Cancel</button>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Bulk Dept Modal */}
      {showBulkModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:560}}>
            <div className="modal-header">
              <div>
                <h2 className="text-lg font-bold">Bulk Department Assign</h2>
                <p className="text-xs text-gray-400">Assign departments to multiple workers at once</p>
              </div>
              <button onClick={() => setShowBulkModal(false)} className="modal-close" type="button"><X size={16} /></button>
            </div>
            <div className="modal-body space-y-5">
              <div>
                <label className="field-label">Action Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['add','➕ Add to Existing','Adds new alongside existing'],['replace','🔄 Replace All','Removes old, keeps only new']].map(([v,t,d]) => (
                    <button key={v} onClick={() => setBulkAction(v)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${bulkAction === v ? (v==='add' ? 'border-blue-400 bg-blue-50' : 'border-orange-400 bg-orange-50') : 'border-gray-100 hover:bg-gray-50'}`}>
                      <p className={`font-semibold text-sm ${bulkAction===v ? (v==='add' ? 'text-blue-700' : 'text-orange-700') : 'text-gray-700'}`}>{t}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{d}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Select Workers {bulkWorkerIds.length > 0 && <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{bulkWorkerIds.length}</span>}</label>
                  <button type="button" onClick={() => setBulkWorkerIds(bulkWorkerIds.length === workers.length ? [] : workers.map(w => w.id))} className="text-xs text-blue-600 hover:underline">
                    {bulkWorkerIds.length === workers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {workers.map(w => (
                    <button key={w.id} type="button" onClick={() => toggleBulkWorker(w.id)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${bulkWorkerIds.includes(w.id) ? 'border-amber-300 bg-amber-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold ${w.is_active ? 'bg-amber-500' : 'bg-gray-400'}`}>{w.name.charAt(0)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{w.name}</p>
                        {w.departments?.length > 0 && <p className="text-xs text-gray-400 truncate">{w.departments.map(d => d.name).join(', ')}</p>}
                      </div>
                      {bulkWorkerIds.includes(w.id) && <Check className="w-4 h-4 text-amber-500" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">Select Departments {bulkDeptIds.length > 0 && <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{bulkDeptIds.length}</span>}</label>
                <div className="flex flex-wrap gap-2">
                  {departments.map(d => (
                    <button key={d.id} type="button" onClick={() => toggleBulkDept(d.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all flex items-center gap-1 ${bulkDeptIds.includes(d.id) ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 bg-white'}`}
                      style={bulkDeptIds.includes(d.id) ? { backgroundColor: d.color } : {}}>
                      {bulkDeptIds.includes(d.id) && <Check className="w-3 h-3" />}
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={handleBulkSave} disabled={bulkSaving || !bulkWorkerIds.length || !bulkDeptIds.length}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">
                  {bulkSaving ? 'Saving...' : `Assign to ${bulkWorkerIds.length} Workers`}
                </button>
                <button onClick={() => setShowBulkModal(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
